import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const InventorySpareParts = ({ axiosInstance, selectedWarehouse, warehouses = [] }) => {
    // State for Stock Spare Part Management
    const [spareParts, setSpareParts] = useState([]);
    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [isEditSparePartOpen, setIsEditSparePartOpen] = useState(false);

    // State for Brand Management
    const [brands, setBrands] = useState([]);

    // State for Spare Part Details Modal (Aggregation)
    const [isSparePartDetailsOpen, setIsSparePartDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelSpareParts, setModelSpareParts] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchSpareParts();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchSpareParts = async () => {
        try {
            const response = await axiosInstance.get('/stock/spare-parts');
            setSpareParts(response.data);
        } catch (error) {
            console.error("Error fetching spare parts:", error);
            toast.error("Failed to fetch spare parts");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/spare-parts/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleRowClick = (aggregatedSparePart) => {
        if (!isSuperAdmin) return;
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const relatedSpareParts = filteredSpareParts.filter(s => 
            normalize(s.model) === normalize(aggregatedSparePart.model) && 
            normalize(s.brand) === normalize(aggregatedSparePart.brand) &&
            normalize(s.itemDescription) === normalize(aggregatedSparePart.itemDescription) &&
            normalize(s.itemCode) === normalize(aggregatedSparePart.itemCode) &&
            (s.warehouse_id || 'null') === (aggregatedSparePart.warehouse_id || 'null')
        );
        setSelectedModel(aggregatedSparePart);
        setModelSpareParts(relatedSpareParts);
        setIsSparePartDetailsOpen(true);
    };

    const handleEditSparePart = (sparePart) => {
        setSelectedSparePart(sparePart);
        setIsEditSparePartOpen(true);
    };

    const handleDeleteSparePart = async (sparePartId) => {
        const confirmed = await confirmDialog({
            title: "Delete Spare Part",
            description: "Are you sure you want to delete this spare part?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/spare-parts/${sparePartId}`);
            toast.success("Spare part deleted successfully");
            
            // Refresh main list
            await fetchSpareParts();
            
            // Update modal list
            setModelSpareParts(prev => prev.filter(s => s.id !== sparePartId));
            
            // If no spare parts left for this model, close modal
            if (modelSpareParts.length <= 1) {
                setIsSparePartDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting spare part:", error);
            toast.error("Failed to delete spare part");
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedSparePart(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateSparePart = async () => {
        if (!selectedSparePart) return;
        
        try {
            const payload = {
                brand: selectedSparePart.brand,
                model: selectedSparePart.model,
                itemDescription: selectedSparePart.itemDescription,
                itemCode: selectedSparePart.itemCode,
                quantity: parseInt(selectedSparePart.quantity) || 0,
                warehouse_id: selectedSparePart.warehouse_id || null
            };

            await axiosInstance.put(`/stock/spare-parts/${selectedSparePart.id}`, payload);
            toast.success("Spare part updated successfully");
            setIsEditSparePartOpen(false);
            setSelectedSparePart(null);
            
            // Refresh main list
            await fetchSpareParts();
            
            // Update local state for details modal if it's open
            if (isSparePartDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/spare-parts');
                const allSpareParts = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelSpareParts = allSpareParts.filter(s => 
                    normalize(s.model) === normalize(selectedModel.model) && 
                    normalize(s.brand) === normalize(selectedModel.brand) &&
                    normalize(s.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(s.itemCode) === normalize(selectedModel.itemCode) &&
                    (s.warehouse_id || 'null') === (selectedModel.warehouse_id || 'null')
                );
                setModelSpareParts(updatedModelSpareParts);
            }
        } catch (error) {
            console.error("Error updating spare part:", error);
            toast.error("Failed to update spare part");
        }
    };

    // Filter and Sort Spare Parts
    const filteredSpareParts = spareParts.filter(sparePart => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? sparePart.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (sparePart.itemCode?.toLowerCase().includes(searchLower) || 
             sparePart.itemDescription?.toLowerCase().includes(searchLower) ||
             sparePart.model?.toLowerCase().includes(searchLower));
        
        // Warehouse Filter
        const matchesWarehouse = !selectedWarehouse || sparePart.warehouse_id === selectedWarehouse.id;

        return matchesBrand && matchesSearch && matchesWarehouse;
    });

    const getWarehouseName = (id) => {
        if (!id) return "Unassigned";
        const w = warehouses.find(w => w.id === id);
        return w ? w.name : "Unknown";
    };

    // Aggregate Spare Parts by Brand + Model + Description + ItemCode + Warehouse
    const aggregatedSpareParts = Object.values(filteredSpareParts.reduce((acc, sparePart) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(sparePart.brand)}-${normalize(sparePart.model)}-${normalize(sparePart.itemDescription)}-${normalize(sparePart.itemCode)}-${sparePart.warehouse_id || 'null'}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...sparePart,
                totalQuantity: 0,
                count: 0
            };
        }
        acc[key].totalQuantity += (sparePart.quantity || 0);
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedSpareParts = aggregatedSpareParts.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.totalQuantity - a.totalQuantity;
            case "lowest_qty":
                return a.totalQuantity - b.totalQuantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.itemCode.localeCompare(b.itemCode);
        }
    });

    const totalQuantity = sortedAggregatedSpareParts.reduce((sum, sparePart) => sum + sparePart.totalQuantity, 0);

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Top Controls Bar */}
            <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
                <div className="w-[200px] space-y-2">
                    <Label>Brand</Label>
                    <Select value={brandFilter} onValueChange={setBrandFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select Brand" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Brands</SelectItem>
                            {brands.map(brand => (
                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 space-y-2 min-w-[200px]">
                    <Label>Search</Label>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search Model, Description, or Item Code..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="flex-1"
                        />
                    </div>
                </div>

                <div className="w-[200px] space-y-2">
                    <Label>Sort By</Label>
                    <Select value={sortOption} onValueChange={setSortOption}>
                        <SelectTrigger>
                            <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                            <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                            <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stock List Table */}
            <div className="bg-white p-4 rounded-lg shadow flex-1 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-orange-600 ml-1">{sortedAggregatedSpareParts.length}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-black font-bold ml-1">{totalQuantity}</span>
                    </div>
                </div>
                <div className="border rounded-md flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-bold">Warehouse</TableHead>
                                <TableHead className="font-bold">Brand</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Part Number</TableHead>
                                <TableHead>Total Quantity</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedAggregatedSpareParts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                                        No spare parts found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedAggregatedSpareParts.map((sparePart, index) => (
                                    <TableRow 
                                        key={`${sparePart.brand}-${sparePart.itemCode}-${index}`} 
                                        className="hover:bg-green-100 cursor-pointer transition-colors"
                                        onClick={() => handleRowClick(sparePart)}
                                    >
                                        <TableCell>{getWarehouseName(sparePart.warehouse_id)}</TableCell>
                                        <TableCell className="font-bold">{sparePart.brand}</TableCell>
                                        <TableCell>{sparePart.itemDescription}</TableCell>
                                        <TableCell>{sparePart.itemCode}</TableCell>
                                        <TableCell>{sparePart.totalQuantity}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Spare Part Details Modal */}
            <Dialog open={isSparePartDetailsOpen} onOpenChange={setIsSparePartDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Spare Part Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Entries: {modelSpareParts.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                {isSuperAdmin && <TableHead>Added By</TableHead>}
                                {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                <TableHead>Warehouse</TableHead>
                                <TableHead>Brand</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Part Number</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelSpareParts.map((sparePart) => (
                                    <TableRow key={sparePart.id}>
                                        {isSuperAdmin && <TableCell>{sparePart.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(sparePart.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{getWarehouseName(sparePart.warehouse_id)}</TableCell>
                                        <TableCell>{sparePart.brand}</TableCell>
                                        <TableCell>{sparePart.itemDescription}</TableCell>
                                        <TableCell>{sparePart.itemCode}</TableCell>
                                        <TableCell>{sparePart.quantity}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditSparePart(sparePart)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteSparePart(sparePart.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsSparePartDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Spare Part Modal */}
            <Dialog open={isEditSparePartOpen} onOpenChange={setIsEditSparePartOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Spare Part</DialogTitle>
                        <DialogDescription>
                            Update the details of the spare part stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedSparePart && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedSparePart.brand} 
                                        onValueChange={(value) => setSelectedSparePart(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-warehouse" className="text-right">
                                    Warehouse
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedSparePart.warehouse_id || "unassigned"} 
                                        onValueChange={(value) => setSelectedSparePart(prev => ({ ...prev, warehouse_id: value === "unassigned" ? null : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Warehouse" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {warehouses.map(warehouse => (
                                                <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedSparePart.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Part Number
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedSparePart.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedSparePart.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateSparePart}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default InventorySpareParts;